import { Box, darken } from '@mui/material';
import { motion } from 'motion/react';
import { FunctionComponent, useEffect, useState } from 'react';
import { Sound, useMusic } from '../../Sound';
import { adaptTransition, AsyncAnimationProps, useAnimationsDisabled, waitForAnimation } from '../../util/Animation';
import { waitForSeconds } from '../../util/Async';
import { Log } from '../../util/Logging';
import { muiStyleSheet } from '../../util/Styles';
import { GameBoardZIndex } from '../game/GameBoardZIndex';

const Durations = {
	logo: 4,
	curtain: 0,
	animsDisabled: 5,
};

export const WelcomeSequence: FunctionComponent<AsyncAnimationProps> = (props) => {
	const [logoComplete, setLogoComplete] = useState(false);
	const [curtainsOpen, setCurtainsOpen] = useState(false);
	const animsDisabled = useAnimationsDisabled();

	useMusic(Sound.music.ThemeSong, props.show);

	useEffect(() => {
		if (props.show) {
			void runAnimation();
		} else {
			setLogoComplete(false);
			setCurtainsOpen(false);
		}

		async function runAnimation() {
			if (!animsDisabled) {
				await Promise.all([
					(async () => {
						// Logo anim
						await waitForAnimation({ seconds: Durations.logo });
						Log.ui.debug('Logo finished');
						setLogoComplete(true);
					})(),
					(async () => {
						// Give the logo a headstart
						await waitForAnimation({ seconds: Durations.logo * 0.8 });
						Log.ui.debug('Curtain start');
						setCurtainsOpen(true);
						await waitForAnimation({ seconds: Durations.curtain });
						Log.ui.debug('Curtain finished');
					})()
				]);
			} else {
				// If animation is disabled, hold initial view on screen for a bit.			
				await waitForSeconds(Durations.animsDisabled);
			}

			Log.ui.debug('WelcomeSequence finished');
			props.onComplete?.();
		}
	}, [props.show]);

	// Log.ui.debug(`Render WelcomeSequence. show: ${props.show} curtainsOpen: ${curtainsOpen} logoComplete: ${logoComplete}`);

	return props.show && <Box sx={styles.container}	>
		{/* <Box sx={[styles.background, gameStyles.boardBackground]} /> */}

		{/* Curtains */}
		<CurtainHalf side="left" open={curtainsOpen} />
		<CurtainHalf side="right" open={curtainsOpen} />

		{/* Logo animation */}
		{logoComplete || <LogoAnimation />}
	</Box>;
};

const LogoAnimation: FunctionComponent = () => {
	const animsDisabled = useAnimationsDisabled();

	const visible = { scale: 1.5, opacity: 1 };
	return <motion.div
		initial={visible}
		animate={animsDisabled ? visible : { scale: 20, opacity: 0 }}
		transition={adaptTransition({ duration: Durations.logo, delay: 2, ease: 'anticipate', })}
		style={styles.logo}
	>
		<img src="/images/Mind Clash Logo.png" alt='' style={styles.logoImage} />
	</motion.div>;
};

type CurtainSegment = {
	height: string;
	skewEnd: number;
	xEnd: string;
	springStiffness: number;
	springDamping: number;
	/** Proportion of the overall duration to wait before starting. Used to give a staggered effect with bottom moving first */
	delay: number;
};

const BASE_COLOR = '#8B0000';

const SEGMENTS: CurtainSegment[] = [
	{ height: '60%', skewEnd: -12, xEnd: '100%', springStiffness: 100, springDamping: 20, delay: .0028 },
	{ height: '20%', skewEnd: -1, xEnd: '110%', springStiffness: 80, springDamping: 15, delay: 0 },
	{ height: '20%', skewEnd: 18, xEnd: '110%', springStiffness: 60, springDamping: 10, delay: .0008 },
];

const CurtainHalf: FunctionComponent<{ side: 'left' | 'right'; open: boolean; }> = (props) => {
	const isLeft = props.side === 'left';
	// Slight overlap to replicate real curtains and reduce artefacts in the gap
	const width = isLeft ? '52%' : '50%';

	// Find the third segment (index 2) for rope animation
	const thirdSegment = SEGMENTS.getSafe(2);

	return <Box sx={[styles.curtain, { [props.side]: 0, width }]}	>
		{SEGMENTS.map((segment, index) =>
			<motion.div
				key={index}
				initial={{
					x: 0,
					skewX: 0,
				}}
				animate={{
					x: props.open ? ((isLeft ? '-' : '') + segment.xEnd) : 0,
					skewX: (isLeft ? 1 : -1) * (props.open ? segment.skewEnd : 0)
				}}
				transition={adaptTransition({
					x: {
						type: 'spring',
						ease: 'easeIn',
						stiffness: segment.springStiffness,
						damping: segment.springDamping,
						duration: Durations.curtain,
						delay: Durations.curtain * segment.delay,
					},
					skewX: {
						type: 'spring',
						stiffness: segment.springStiffness,
						damping: segment.springDamping,
						duration: Durations.curtain,
					},
				})}
				style={{
					height: segment.height,
					width: '110%',
					background: `linear-gradient(${isLeft ? '90deg' : '270deg'}, ${BASE_COLOR} 0%, #DC143C 50%, ${BASE_COLOR} 100%)`,
					// Horizontal 'shadows' to give some depth
					boxShadow: `inset ${isLeft ? '-20px' : '20px'} 0 40px ${darken(BASE_COLOR, .05)}`,
					borderLeft: isLeft ? 'none' : `5px solid ${darken(BASE_COLOR, .2)}`,
					overflow: 'hidden',
					transformOrigin: 'top',
				}}>
				<Box sx={styles.velvetOverlay} />
				<Box sx={styles.velvetFolds} />
			</motion.div>
		)}
		{/* Rope positioned at the join between second and third segments */}
		<motion.div
			initial={{ x: 0 }}
			animate={{
				x: props.open ? ((isLeft ? '-' : '') + thirdSegment.xEnd) : 0,
			}}
			transition={adaptTransition({
				x: {
					type: 'spring',
					ease: 'easeIn',
					stiffness: thirdSegment.springStiffness,
					damping: thirdSegment.springDamping,
					duration: Durations.curtain,
					delay: Durations.curtain * thirdSegment.delay,
				},
			})}
			style={styles.rope}
		/>
	</Box >;
};

const styles = muiStyleSheet({
	container: {
		position: 'fixed',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		zIndex: GameBoardZIndex.Transition,
	},
	background: {
		height: '100%',
		width: '100%',
	},
	logo: {
		position: 'absolute',
		x: '-50%', y: '-50%',
		top: '50%', left: '50%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	logoImage: {
		maxWidth: '400px',
		maxHeight: '400px',
	},
	curtain: {
		position: 'absolute',
		top: 0,
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
	},
	velvetOverlay: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		background: 'repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(0, 0, 0, 0.1) 2px, rgba(0, 0, 0, 0.1) 4px)',
		opacity: 0.3,
	},
	velvetFolds: {
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		background: 'repeating-linear-gradient(90deg, rgba(255, 255, 255, 0.05) 0px, rgba(255, 255, 255, 0.05) 40px, rgba(0, 0, 0, 0.1) 40px, rgba(0, 0, 0, 0.1) 80px)',
	},
	rope: {
		position: 'absolute',
		left: 0,
		width: '110%',
		height: '16px',
		top: '80%',
		backgroundImage: 'url("/images/Red Rope.png")',
		backgroundRepeat: 'repeat-x',
		backgroundSize: 'auto 100%',
		filter: 'brightness(0.75)',
	}
});